import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;
const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Settings → My Settings → My Pay Statements: every finalized pay run that includes me, with PDF download.
export default function MyPayStatements() {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pay-runs/mine`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setItems(r.data.statements)).catch(() => setItems([]));
  }, [token]);
  const download = async (s) => {
    try {
      const r = await axios.get(`${API}/pay-runs/${s.run_id}/statement/${user.id}.pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `pay-statement-${s.start_date}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Download failed', description: e.message, variant: 'destructive' }); }
  };
  return (
    <div className="rounded-xl p-5 space-y-4" style={{ background: '#111C2A', border: '1px solid #2D3748' }} data-testid="my-pay-statements">
      <div>
        <h3 className="text-lg font-semibold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-400" /> My pay statements</h3>
        <p className="text-xs" style={{ color: '#94A3B8' }}>Hours and gross earnings for each finalized pay period, before deductions. Your net pay and tax slips come from the payroll provider.</p>
      </div>
      {items === null ? <p className="text-sm text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="my-pay-statements-empty">No finalized pay statements yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead style={{ color: '#94A3B8' }}><tr className="text-left text-xs"><th className="py-1">Period</th><th className="py-1 text-right">Hours</th><th className="py-1 text-right">Overtime</th><th className="py-1 text-right">Vacation</th><th className="py-1 text-right">Gross</th><th className="py-1" /></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.run_id} className="border-t" style={{ borderColor: '#1F2D3F' }} data-testid={`my-pay-statement-${s.run_id}`}>
                <td className="py-2 text-white">{s.start_date} → {s.end_date}</td>
                <td className="py-2 text-right text-white">{s.total_hours?.toFixed(2)}</td>
                <td className="py-2 text-right text-amber-300">{s.overtime_hours?.toFixed(2)}</td>
                <td className="py-2 text-right text-violet-300">{money(s.vacation_pay_amount)} <span className="text-[10px] text-slate-500">{s.vacation_pay_mode === 'payout' ? 'paid' : 'accrued'}</span></td>
                <td className="py-2 text-right text-emerald-300 font-semibold">{money(s.gross_pay)}</td>
                <td className="py-2 text-right"><button type="button" className="inline-flex items-center gap-1 text-xs underline text-sky-300" onClick={() => download(s)} data-testid={`my-pay-statement-pdf-${s.run_id}`}><Download className="w-3 h-3" /> PDF</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
